import React, { useEffect, useState } from 'react';
import { PaystackScript } from '../components/Payment/PaystackScript';
import { Check, Users, User, BookOpen, Zap, Target, Award, Clock, TrendingUp, Code, BarChart, ArrowRight, X } from 'lucide-react';
export const AIMasterclass = () => {
  const [selectedPlan, setSelectedPlan] = useState<'earlyBird' | 'regular' | 'oneOnOne' | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'form' | 'success'>('pending');
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [expandedTrack, setExpandedTrack] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: ''
  });
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyBar(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleFormChange = (e: any) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handlePlanSelect = (plan: 'earlyBird' | 'regular' | 'oneOnOne') => {
    setSelectedPlan(plan);
    setPaymentStatus('form');
    window.scrollTo(0, 0);
  };
  const initializePayment = () => {
    if (!formData.fullName || !formData.email || !formData.phone) {
      alert('Please fill in all required fields');
      return;
    }
    if (!(window as any).PaystackPop) {
      alert('Payment system loading, please try again in a moment');
      return;
    }
    const amount = selectedPlan === 'earlyBird' ? 100000 : selectedPlan === 'regular' ? 150000 : 250000;
    const handler = (window as any).PaystackPop.setup({
      key: 'pk_live_c2f71547b29d95243a012d9ef2f8e03485d942e7',
      email: formData.email,
      amount: amount * 100,
      currency: 'NGN',
      metadata: {
        custom_fields: [{
          display_name: 'Full Name',
          variable_name: 'full_name',
          value: formData.fullName
        }, {
          display_name: 'Phone',
          variable_name: 'phone',
          value: formData.phone
        }, {
          display_name: 'Plan',
          variable_name: 'plan',
          value: selectedPlan === 'earlyBird' ? 'Early Bird - ₦100,000' : selectedPlan === 'regular' ? 'Regular - ₦150,000' : 'One-on-One - ₦250,000'
        }]
      },
      callback: (response: any) => {
        setPaymentStatus('success');
        window.location.href = 'https://chat.whatsapp.com/GoKjklvily1CfnpN12X0C0';
      },
      onClose: () => {
        console.log('Payment closed');
      }
    });
    handler.openIframe();
  };
  const audienceSegments = [{
    title: 'Professionals & Employees',
    benefit: 'Stay relevant and competitive in the AI era',
    image: 'https://cdn.magicpatterns.com/uploads/b5qbZrnP5irMRaRFpRKjaC/career.png',
    alt: 'Professional African employees working together'
  }, {
    title: 'Creators & Freelancers',
    benefit: 'Gain high-value, monetizable AI skills',
    image: 'https://cdn.magicpatterns.com/uploads/wXscmP3ENUgo5eW9yRpwu4/www.ayevbosaandabiodun.wed.png',
    alt: 'African content creator working on laptop'
  }, {
    title: 'Entrepreneurs & Business Owners',
    benefit: 'Build smarter systems and scale faster',
    image: 'https://cdn.magicpatterns.com/uploads/4KDx2ctpCq5fSwB9DT2PzT/www.ayevbosaandabiodun.wedl.png',
    alt: 'African entrepreneurs collaborating'
  }, {
    title: 'Beginners & Career Switchers',
    benefit: 'Start your AI journey with confidence',
    image: 'https://cdn.magicpatterns.com/uploads/jBtb9et5sitSh2L3ksGGgh/career_entry.png',
    alt: 'African professionals learning together'
  }];
  const tracks = [{
    icon: <Zap size={32} data-id="element-1591" />,
    title: 'AI Foundations & Prompting',
    points: ['How modern AI tools actually work', 'Prompt frameworks for work, business, and creation', 'Thinking and working effectively with AI']
  }, {
    icon: <BarChart size={32} data-id="element-1592" />,
    title: 'AI for Data Analysis',
    points: ['Using AI to analyze datasets', 'Generating insights, summaries, and reports', 'Decision-making with AI support']
  }, {
    icon: <Code size={32} data-id="element-1593" />,
    title: 'AI for Web Development',
    points: ['Building websites with AI-assisted tools', 'Structuring pages, layouts, and copy', 'No-code and low-code workflows']
  }, {
    icon: <BookOpen size={32} data-id="element-1594" />,
    title: 'AI for App Development',
    points: ['Turning ideas into functional apps', 'AI-assisted logic, flows, and prototyping', 'Building without heavy coding']
  }, {
    icon: <TrendingUp size={32} data-id="element-1595" />,
    title: 'AI for Productivity & Business',
    points: ['Automations and workflows', 'Content, marketing, and branding', 'AI as a daily work assistant']
  }];
  if (paymentStatus === 'success') {
    return <div className="min-h-screen flex items-center justify-center px-4" style={{
      backgroundColor: '#F9F9F9'
    }} data-id="element-1596">
        <div className="text-center p-6 md:p-8 max-w-md" data-id="element-1597">
          <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-6 rounded-full flex items-center justify-center animate-scale-in" style={{
          backgroundColor: '#1AB9C8'
        }} data-id="element-1598">
            <Check size={40} style={{
            color: '#FFFFFF'
          }} data-id="element-1599" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-4" style={{
          color: '#0B1F3F'
        }} data-id="element-1600">
            Payment Successful!
          </h1>
          <p className="text-base md:text-lg mb-4" style={{
          color: '#333333'
        }} data-id="element-1601">
            Welcome to the AI Accelerator Program!
          </p>
          <p className="text-sm md:text-base" style={{
          color: '#333333'
        }} data-id="element-1602">
            Redirecting you to WhatsApp to join the cohort...
          </p>
        </div>
      </div>;
  }
  if (paymentStatus === 'form') {
    return <div className="min-h-screen py-8 md:py-12 px-4" style={{
      backgroundColor: '#F9F9F9'
    }} data-id="element-1603">
        <div className="container mx-auto max-w-2xl" data-id="element-1604">
          <div className="mb-6 md:mb-8 text-center" data-id="element-1605">
            <h1 className="text-2xl md:text-3xl font-bold mb-4" style={{
            color: '#0B1F3F'
          }} data-id="element-1606">
              Complete Your Registration
            </h1>
            <p className="text-base md:text-lg mb-2" style={{
            color: '#333333'
          }} data-id="element-1607">
              {selectedPlan === 'earlyBird' ? 'Early Bird - ₦100,000' : selectedPlan === 'regular' ? 'Regular Price - ₦150,000' : 'One-on-One Session - ₦250,000'}
            </p>
          </div>

          <div className="p-6 md:p-8 rounded-lg" style={{
          backgroundColor: '#FFFFFF',
          border: '2px solid #E5E5E5'
        }} data-id="element-1608">
            <div className="space-y-4 md:space-y-6 mb-6 md:mb-8" data-id="element-1609">
              <div data-id="element-1610">
                <label className="block mb-2 font-medium text-sm md:text-base" style={{
                color: '#333333'
              }} data-id="element-1611">
                  Full Name *
                </label>
                <input type="text" name="fullName" value={formData.fullName} onChange={handleFormChange} required className="w-full p-3 rounded-md text-sm md:text-base" style={{
                border: '2px solid #E5E5E5'
              }} placeholder="Enter your full name" data-id="element-1612" />
              </div>

              <div data-id="element-1613">
                <label className="block mb-2 font-medium text-sm md:text-base" style={{
                color: '#333333'
              }} data-id="element-1614">
                  Email Address *
                </label>
                <input type="email" name="email" value={formData.email} onChange={handleFormChange} required className="w-full p-3 rounded-md text-sm md:text-base" style={{
                border: '2px solid #E5E5E5'
              }} placeholder="Enter your email" data-id="element-1615" />
              </div>

              <div data-id="element-1616">
                <label className="block mb-2 font-medium text-sm md:text-base" style={{
                color: '#333333'
              }} data-id="element-1617">
                  Phone Number *
                </label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleFormChange} required className="w-full p-3 rounded-md text-sm md:text-base" style={{
                border: '2px solid #E5E5E5'
              }} placeholder="Enter your phone number" data-id="element-1618" />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 md:gap-4" data-id="element-1619">
              <button type="button" onClick={() => {
              setPaymentStatus('pending');
              setSelectedPlan(null);
            }} className="flex-1 py-3 md:py-4 rounded-md font-bold text-sm md:text-base transition-all hover:opacity-90" style={{
              backgroundColor: '#E5E5E5',
              color: '#333333'
            }} data-id="element-1620">
                ← Back
              </button>
              <button onClick={initializePayment} className="flex-1 py-3 md:py-4 rounded-md font-bold text-white text-sm md:text-base transition-all hover:opacity-90 hover:scale-105" style={{
              backgroundColor: '#0B1F3F'
            }} data-id="element-1621">
                Pay{' '}
                {selectedPlan === 'earlyBird' ? '₦100,000' : selectedPlan === 'regular' ? '₦150,000' : '₦250,000'}
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  return <>
      <PaystackScript data-id="element-1622" />
      <div className="min-h-screen" style={{
      backgroundColor: '#F9F9F9'
    }} data-id="element-1623">
        {/* Sticky CTA Bar */}
        {showStickyBar && <div className="fixed bottom-0 left-0 right-0 z-50 p-4 shadow-lg animate-slide-up" style={{
        backgroundColor: '#0B1F3F'
      }} data-id="element-1624">
            <div className="container mx-auto flex flex-col sm:flex-row items-center justify-between gap-3" data-id="element-1625">
              <p className="text-white text-sm md:text-base font-medium text-center sm:text-left" data-id="element-1626">
                Limited to 20 participants • Early Bird: ₦100,000
              </p>
              <div className="flex gap-2 w-full sm:w-auto" data-id="element-1627">
                <button onClick={() => handlePlanSelect('earlyBird')} className="flex-1 sm:flex-none px-4 md:px-6 py-2 md:py-3 rounded-md font-bold text-sm md:text-base transition-all hover:scale-105" style={{
              backgroundColor: '#F4B400',
              color: '#FFFFFF'
            }} data-id="element-1628">
                  Secure Slot
                </button>
              </div>
            </div>
          </div>}

        {/* Hero Section */}
        <section className="relative py-12 md:py-20 overflow-hidden px-4" data-id="element-1629">
          <div className="absolute inset-0 z-0" data-id="element-1630">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/bNxF5qHdCS6UJLX4hcirCA/IMG_7209.jpg" alt="AI Accelerator Program" className="w-full h-full object-cover grayscale opacity-20" data-id="element-1631" />
          </div>
          <div className="container mx-auto relative z-10" data-id="element-1632">
            <div className="max-w-4xl mx-auto text-center animate-fade-in" data-id="element-1633">
              <div className="inline-block px-3 md:px-4 py-1 md:py-2 rounded-full mb-4 md:mb-6 text-xs md:text-sm" style={{
              backgroundColor: '#F4B400',
              color: '#FFFFFF'
            }} data-id="element-1634">
                <span className="font-bold" data-id="element-1635">COHORT-BASED ACCELERATOR</span>
              </div>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6" style={{
              color: '#0B1F3F'
            }} data-id="element-1636">
                AI Accelerator Program
              </h1>
              <p className="text-lg md:text-xl lg:text-2xl mb-4 md:mb-8" style={{
              color: '#333333'
            }} data-id="element-1637">
                Build, Deploy & Work Smarter With AI — Faster
              </p>
              <p className="text-sm md:text-base lg:text-lg mb-4 md:mb-8 max-w-3xl mx-auto" style={{
              color: '#333333'
            }} data-id="element-1638">
                A hands-on, cohort-based learning program designed to help
                professionals, entrepreneurs, creators, and beginners rapidly
                apply AI to real work, business, and digital projects.
              </p>
              <p className="text-base md:text-lg lg:text-xl font-bold mb-6 md:mb-8" style={{
              color: '#1AB9C8'
            }} data-id="element-1639">
                This is not theory. This is execution-focused acceleration.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center" data-id="element-1640">
                <button onClick={() => handlePlanSelect('earlyBird')} className="px-6 md:px-8 py-3 md:py-4 rounded-md font-bold text-sm md:text-base lg:text-lg transition-all hover:scale-105 hover:shadow-lg" style={{
                backgroundColor: '#F4B400',
                color: '#FFFFFF'
              }} data-id="element-1641">
                  Secure Early Bird Slot
                </button>
                <button onClick={() => document.getElementById('pricing')?.scrollIntoView({
                behavior: 'smooth'
              })} className="px-6 md:px-8 py-3 md:py-4 rounded-md font-bold text-sm md:text-base lg:text-lg transition-all hover:scale-105" style={{
                backgroundColor: '#FFFFFF',
                color: '#0B1F3F',
                border: '2px solid #0B1F3F'
              }} data-id="element-1642">
                  View Pricing
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* What It Is Section */}
        <section className="py-12 md:py-16 px-4" style={{
        backgroundColor: '#FFFFFF'
      }} data-id="element-1643">
          <div className="container mx-auto" data-id="element-1644">
            <div className="max-w-4xl mx-auto" data-id="element-1645">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-6 md:mb-8 animate-fade-in" style={{
              color: '#0B1F3F'
            }} data-id="element-1646">
                What the AI Accelerator Program Is
              </h2>
              <div className="text-center mb-8 md:mb-12" data-id="element-1647">
                <p className="text-lg md:text-xl mb-4 md:mb-6" style={{
                color: '#333333'
              }} data-id="element-1648">
                  The AI Accelerator Program helps you move from:
                </p>
                <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-4 text-base md:text-lg font-bold" data-id="element-1649">
                  <span className="px-4 py-2 rounded-md" style={{
                  color: '#0B1F3F',
                  backgroundColor: '#F0F0F0'
                }} data-id="element-1650">
                    AI awareness
                  </span>
                  <ArrowRight size={24} style={{
                  color: '#1AB9C8'
                }} className="rotate-90 md:rotate-0" data-id="element-1651" />
                  <span className="px-4 py-2 rounded-md" style={{
                  color: '#0B1F3F',
                  backgroundColor: '#F0F0F0'
                }} data-id="element-1652">
                    AI application
                  </span>
                  <ArrowRight size={24} style={{
                  color: '#1AB9C8'
                }} className="rotate-90 md:rotate-0" data-id="element-1653" />
                  <span className="px-4 py-2 rounded-md" style={{
                  color: '#0B1F3F',
                  backgroundColor: '#F0F0F0'
                }} data-id="element-1654">
                    AI-powered results
                  </span>
                </div>
              </div>
              <p className="text-base md:text-lg text-center max-w-3xl mx-auto" style={{
              color: '#333333'
            }} data-id="element-1655">
                You will learn how to use AI as a{' '}
                <strong data-id="element-1656">business partner</strong>,{' '}
                <strong data-id="element-1657">productivity engine</strong>, and{' '}
                <strong data-id="element-1658">development tool</strong> — without needing a technical
                background.
              </p>
            </div>
          </div>
        </section>

        {/* Who This Is For Section - IMAGE-LED */}
        <section className="py-12 md:py-16 px-4" style={{
        backgroundColor: '#F9F9F9'
      }} data-id="element-1659">
          <div className="container mx-auto" data-id="element-1660">
            <div className="max-w-6xl mx-auto" data-id="element-1661">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-4 md:mb-6" style={{
              color: '#0B1F3F'
            }} data-id="element-1662">
                Who This Program Is For
              </h2>
              <p className="text-base md:text-lg text-center mb-8 md:mb-12 max-w-2xl mx-auto" style={{
              color: '#333333'
            }} data-id="element-1663">
                See yourself in the AI revolution. This program is built for
                people like you.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8" data-id="element-1664">
                {audienceSegments.map((segment, i) => <div key={i} className="group rounded-lg overflow-hidden transition-all hover:scale-105 hover:shadow-xl cursor-pointer animate-fade-in" style={{
                backgroundColor: '#FFFFFF',
                border: '2px solid #E5E5E5',
                animationDelay: `${i * 100}ms`
              }} data-id="element-1665">
                    <div className="aspect-square overflow-hidden" data-id="element-1666">
                      <img src={segment.image} alt={segment.alt} className="w-full h-full object-cover transition-transform group-hover:scale-110" data-id="element-1667" />
                    </div>
                    <div className="p-4 md:p-6" data-id="element-1668">
                      <h3 className="text-base md:text-lg font-bold mb-2" style={{
                    color: '#0B1F3F'
                  }} data-id="element-1669">
                        {segment.title}
                      </h3>
                      <p className="text-sm md:text-base" style={{
                    color: '#666'
                  }} data-id="element-1670">
                        {segment.benefit}
                      </p>
                    </div>
                  </div>)}
              </div>
              <p className="text-center text-base md:text-lg font-bold" style={{
              color: '#1AB9C8'
            }} data-id="element-1671">
                No prior coding or AI experience required.
              </p>
            </div>
          </div>
        </section>

        {/* What You'll Learn Section - EXPANDABLE */}
        <section className="py-12 md:py-16 px-4" style={{
        backgroundColor: '#FFFFFF'
      }} data-id="element-1672">
          <div className="container mx-auto" data-id="element-1673">
            <div className="max-w-5xl mx-auto" data-id="element-1674">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-3 md:mb-4" style={{
              color: '#0B1F3F'
            }} data-id="element-1675">
                What You'll Learn
              </h2>
              <p className="text-center text-lg md:text-xl mb-8 md:mb-12" style={{
              color: '#333333'
            }} data-id="element-1676">
                5 Core Accelerator Tracks
              </p>
              <div className="space-y-3 md:space-y-4" data-id="element-1677">
                {tracks.map((track, i) => <div key={i} className="rounded-lg overflow-hidden transition-all hover:shadow-lg cursor-pointer" style={{
                backgroundColor: '#F9F9F9',
                border: '2px solid #E5E5E5'
              }} onClick={() => setExpandedTrack(expandedTrack === i ? null : i)} data-id="element-1678">
                    <div className="p-4 md:p-6 flex items-center justify-between" data-id="element-1679">
                      <div className="flex items-center gap-3 md:gap-4 flex-1" data-id="element-1680">
                        <div className="p-2 md:p-3 rounded-full flex-shrink-0" style={{
                      backgroundColor: '#1AB9C8',
                      color: '#FFFFFF'
                    }} data-id="element-1681">
                          {track.icon}
                        </div>
                        <h3 className="text-base md:text-lg lg:text-xl font-bold" style={{
                      color: '#0B1F3F'
                    }} data-id="element-1682">
                          {track.title}
                        </h3>
                      </div>
                      <div className="transition-transform" style={{
                    transform: expandedTrack === i ? 'rotate(180deg)' : 'rotate(0deg)'
                  }} data-id="element-1683">
                        <ArrowRight size={24} style={{
                      color: '#1AB9C8',
                      transform: 'rotate(90deg)'
                    }} data-id="element-1684" />
                      </div>
                    </div>
                    {expandedTrack === i && <div className="px-4 md:px-6 pb-4 md:pb-6 animate-slide-down" data-id="element-1685">
                        <ul className="space-y-2 ml-12 md:ml-16" data-id="element-1686">
                          {track.points.map((point, j) => <li key={j} className="flex items-start gap-2" data-id="element-1687">
                              <Check size={16} style={{
                        color: '#1AB9C8',
                        marginTop: '4px',
                        flexShrink: 0
                      }} data-id="element-1688" />
                              <span className="text-sm md:text-base" style={{
                        color: '#333333'
                      }} data-id="element-1689">
                                {point}
                              </span>
                            </li>)}
                        </ul>
                      </div>}
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        {/* How You'll Learn Section */}
        <section className="py-12 md:py-16 px-4" style={{
        backgroundColor: '#F9F9F9'
      }} data-id="element-1690">
          <div className="container mx-auto" data-id="element-1691">
            <div className="max-w-4xl mx-auto" data-id="element-1692">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-8 md:mb-12" style={{
              color: '#0B1F3F'
            }} data-id="element-1693">
                How You'll Learn
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8" data-id="element-1694">
                {['Live Classes (interactive & instructor-led)', 'Step-by-step demonstrations', 'Hands-on exercises and deliverables', 'Practical templates, prompts, and frameworks', 'Community support throughout the cohort'].map((item, i) => <div key={i} className="flex items-start gap-3 p-4 md:p-5 rounded-lg transition-all hover:scale-105 hover:shadow-md animate-fade-in" style={{
                backgroundColor: '#FFFFFF',
                border: '2px solid #E5E5E5',
                animationDelay: `${i * 100}ms`
              }} data-id="element-1695">
                    <Check size={20} style={{
                  color: '#1AB9C8',
                  marginTop: '2px',
                  flexShrink: 0
                }} data-id="element-1696" />
                    <span className="text-sm md:text-base" style={{
                  color: '#333333'
                }} data-id="element-1697">
                      {item}
                    </span>
                  </div>)}
              </div>
              <p className="text-center text-lg md:text-xl font-bold" style={{
              color: '#0B1F3F'
            }} data-id="element-1698">
                This is a build-as-you-learn program, not passive content.
              </p>
            </div>
          </div>
        </section>

        {/* Pricing Section - 3 OPTIONS IN ONE ROW */}
        <section id="pricing" className="py-12 md:py-16 px-4" style={{
        backgroundColor: '#FFFFFF'
      }} data-id="element-1699">
          <div className="container mx-auto" data-id="element-1700">
            <div className="max-w-7xl mx-auto" data-id="element-1701">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-3 md:mb-4" style={{
              color: '#0B1F3F'
            }} data-id="element-1702">
                Pricing & Enrollment
              </h2>
              <p className="text-center text-base md:text-lg mb-8 md:mb-12" style={{
              color: '#333333'
            }} data-id="element-1703">
                Choose the learning path that works best for you
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-6 mb-6 md:mb-8" data-id="element-1704">
                {/* Early Bird */}
                <div className="p-6 md:p-8 rounded-lg relative transition-all hover:scale-105 hover:shadow-xl" style={{
                backgroundColor: '#FFFBF0',
                border: '3px solid #F4B400'
              }} data-id="element-1705">
                  <div className="absolute -top-3 md:-top-4 left-1/2 transform -translate-x-1/2 px-3 md:px-4 py-1 rounded-full text-xs md:text-sm" style={{
                  backgroundColor: '#F4B400',
                  color: '#FFFFFF'
                }} data-id="element-1706">
                    <span className="font-bold" data-id="element-1707">EARLY BIRD</span>
                  </div>
                  <div className="text-center mb-4 md:mb-6 mt-2 md:mt-4" data-id="element-1708">
                    <p className="text-3xl md:text-4xl font-bold mb-2" style={{
                    color: '#0B1F3F'
                  }} data-id="element-1709">
                      ₦100,000
                    </p>
                    <p className="text-base md:text-lg" style={{
                    color: '#666'
                  }} data-id="element-1710">
                      (≈ $65)
                    </p>
                    <p className="text-xs md:text-sm mt-2" style={{
                    color: '#333333'
                  }} data-id="element-1711">
                      Group Session
                    </p>
                  </div>

                  <ul className="space-y-2 md:space-y-3 mb-6 md:mb-8" data-id="element-1712">
                    {['Full AI Accelerator Program', 'All 5 core tracks', 'Live group instruction', 'Templates & frameworks', 'Community support', 'Save ₦50,000'].map((feature, i) => <li key={i} className="flex items-start gap-2" data-id="element-1713">
                        <Check size={18} style={{
                      color: '#F4B400',
                      marginTop: '2px',
                      flexShrink: 0
                    }} data-id="element-1714" />
                        <span className="text-sm md:text-base" style={{
                      color: '#333333'
                    }} data-id="element-1715">
                          {feature}
                        </span>
                      </li>)}
                  </ul>

                  <button onClick={() => handlePlanSelect('earlyBird')} className="w-full py-3 md:py-4 rounded-md font-bold text-sm md:text-base lg:text-lg transition-all hover:scale-105 hover:shadow-lg" style={{
                  backgroundColor: '#F4B400',
                  color: '#FFFFFF'
                }} data-id="element-1716">
                    Secure Early Bird
                  </button>
                </div>

                {/* Regular Price */}
                <div className="p-6 md:p-8 rounded-lg relative transition-all hover:scale-105 hover:shadow-xl" style={{
                backgroundColor: '#F0FAFB',
                border: '3px solid #1AB9C8'
              }} data-id="element-1717">
                  <div className="absolute -top-3 md:-top-4 left-1/2 transform -translate-x-1/2 px-3 md:px-4 py-1 rounded-full text-xs md:text-sm" style={{
                  backgroundColor: '#1AB9C8',
                  color: '#FFFFFF'
                }} data-id="element-1718">
                    <span className="font-bold" data-id="element-1719">REGULAR</span>
                  </div>
                  <div className="text-center mb-4 md:mb-6 mt-2 md:mt-4" data-id="element-1720">
                    <p className="text-3xl md:text-4xl font-bold mb-2" style={{
                    color: '#0B1F3F'
                  }} data-id="element-1721">
                      ₦150,000
                    </p>
                    <p className="text-base md:text-lg" style={{
                    color: '#666'
                  }} data-id="element-1722">
                      (≈ $100)
                    </p>
                    <p className="text-xs md:text-sm mt-2" style={{
                    color: '#333333'
                  }} data-id="element-1723">
                      Group Session
                    </p>
                  </div>

                  <ul className="space-y-2 md:space-y-3 mb-6 md:mb-8" data-id="element-1724">
                    {['Full AI Accelerator Program', 'All 5 core tracks', 'Live group instruction', 'Templates & frameworks', 'Community support', 'Real deliverables'].map((feature, i) => <li key={i} className="flex items-start gap-2" data-id="element-1725">
                        <Check size={18} style={{
                      color: '#1AB9C8',
                      marginTop: '2px',
                      flexShrink: 0
                    }} data-id="element-1726" />
                        <span className="text-sm md:text-base" style={{
                      color: '#333333'
                    }} data-id="element-1727">
                          {feature}
                        </span>
                      </li>)}
                  </ul>

                  <button onClick={() => handlePlanSelect('regular')} className="w-full py-3 md:py-4 rounded-md font-bold text-sm md:text-base lg:text-lg transition-all hover:scale-105 hover:shadow-lg" style={{
                  backgroundColor: '#1AB9C8',
                  color: '#FFFFFF'
                }} data-id="element-1728">
                    Apply Now
                  </button>
                </div>

                {/* One-on-One */}
                <div className="p-6 md:p-8 rounded-lg relative transition-all hover:scale-105 hover:shadow-xl" style={{
                backgroundColor: '#F5F0FF',
                border: '3px solid #0B1F3F'
              }} data-id="element-1729">
                  <div className="absolute -top-3 md:-top-4 left-1/2 transform -translate-x-1/2 px-3 md:px-4 py-1 rounded-full text-xs md:text-sm" style={{
                  backgroundColor: '#0B1F3F',
                  color: '#FFFFFF'
                }} data-id="element-1730">
                    <span className="font-bold" data-id="element-1731">PREMIUM</span>
                  </div>
                  <div className="text-center mb-4 md:mb-6 mt-2 md:mt-4" data-id="element-1732">
                    <p className="text-3xl md:text-4xl font-bold mb-2" style={{
                    color: '#0B1F3F'
                  }} data-id="element-1733">
                      ₦250,000
                    </p>
                    <p className="text-base md:text-lg" style={{
                    color: '#666'
                  }} data-id="element-1734">
                      (≈ $165)
                    </p>
                    <p className="text-xs md:text-sm mt-2" style={{
                    color: '#333333'
                  }} data-id="element-1735">
                      One-on-One Session
                    </p>
                  </div>

                  <ul className="space-y-2 md:space-y-3 mb-6 md:mb-8" data-id="element-1736">
                    {['Everything in Regular', 'Private 1-on-1 training', 'Personalized strategy', 'Custom implementation', 'Priority support (3 months)', 'Flexible scheduling'].map((feature, i) => <li key={i} className="flex items-start gap-2" data-id="element-1737">
                        <Check size={18} style={{
                      color: '#0B1F3F',
                      marginTop: '2px',
                      flexShrink: 0
                    }} data-id="element-1738" />
                        <span className="text-sm md:text-base" style={{
                      color: '#333333'
                    }} data-id="element-1739">
                          {feature}
                        </span>
                      </li>)}
                  </ul>

                  <button onClick={() => handlePlanSelect('oneOnOne')} className="w-full py-3 md:py-4 rounded-md font-bold text-sm md:text-base lg:text-lg transition-all hover:scale-105 hover:shadow-lg" style={{
                  backgroundColor: '#0B1F3F',
                  color: '#FFFFFF'
                }} data-id="element-1740">
                    Book 1-on-1
                  </button>
                </div>
              </div>

              <div className="text-center p-4 md:p-6 rounded-lg" style={{
              backgroundColor: '#F9F9F9',
              border: '2px solid #E5E5E5'
            }} data-id="element-1741">
                <p className="text-base md:text-lg mb-2" style={{
                color: '#0B1F3F'
              }} data-id="element-1742">
                  <strong data-id="element-1743">Group Cohort Size:</strong> 20 participants only
                </p>
                <p className="text-sm md:text-base" style={{
                color: '#333333'
              }} data-id="element-1744">
                  Once slots are filled, enrollment closes. A waiting list opens
                  automatically for the next cohort.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why It Matters Section */}
        <section className="py-12 md:py-16 px-4" style={{
        backgroundColor: '#0B1F3F'
      }} data-id="element-1745">
          <div className="container mx-auto" data-id="element-1746">
            <div className="max-w-3xl mx-auto text-center" data-id="element-1747">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-6" style={{
              color: '#FFFFFF'
            }} data-id="element-1748">
                Why the AI Accelerator Matters
              </h2>
              <p className="text-lg md:text-xl mb-6 md:mb-8" style={{
              color: '#FFFFFF',
              opacity: 0.9
            }} data-id="element-1749">
                AI will not replace people —{' '}
                <strong data-id="element-1750">
                  People who know how to deploy AI will replace those who don't.
                </strong>
              </p>
              <p className="text-base md:text-lg" style={{
              color: '#FFFFFF',
              opacity: 0.9
            }} data-id="element-1751">
                This program is designed to help you move faster, build smarter,
                and stay relevant.
              </p>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-12 md:py-16 px-4" style={{
        backgroundColor: '#F4B400'
      }} data-id="element-1752">
          <div className="container mx-auto" data-id="element-1753">
            <div className="max-w-4xl mx-auto text-center" data-id="element-1754">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-6 md:mb-8" style={{
              color: '#0B1F3F'
            }} data-id="element-1755">
                Ready to Accelerate?
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4" data-id="element-1756">
                <button onClick={() => handlePlanSelect('earlyBird')} className="py-3 md:py-4 px-4 md:px-6 rounded-md font-bold text-sm md:text-base lg:text-lg transition-all hover:scale-105 hover:shadow-lg" style={{
                backgroundColor: '#0B1F3F',
                color: '#FFFFFF'
              }} data-id="element-1757">
                  Apply for the Program
                </button>
                <button onClick={() => handlePlanSelect('earlyBird')} className="py-3 md:py-4 px-4 md:px-6 rounded-md font-bold text-sm md:text-base lg:text-lg transition-all hover:scale-105 hover:shadow-lg" style={{
                backgroundColor: '#0B1F3F',
                color: '#FFFFFF'
              }} data-id="element-1758">
                  Secure Early Bird Slot
                </button>
                <button onClick={() => window.location.href = 'https://chat.whatsapp.com/GoKjklvily1CfnpN12X0C0'} className="py-3 md:py-4 px-4 md:px-6 rounded-md font-bold text-sm md:text-base lg:text-lg transition-all hover:scale-105 hover:shadow-lg" style={{
                backgroundColor: '#FFFFFF',
                color: '#0B1F3F'
              }} data-id="element-1759">
                  Join Waiting List
                </button>
              </div>
            </div>
          </div>
        </section>

        <style data-id="element-1760">{`
          @keyframes fade-in {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes slide-up {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
          @keyframes slide-down {
            from { max-height: 0; opacity: 0; }
            to { max-height: 500px; opacity: 1; }
          }
          @keyframes scale-in {
            from { transform: scale(0.8); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
          .animate-fade-in {
            animation: fade-in 0.6s ease-out forwards;
          }
          .animate-slide-up {
            animation: slide-up 0.3s ease-out;
          }
          .animate-slide-down {
            animation: slide-down 0.3s ease-out;
          }
          .animate-scale-in {
            animation: scale-in 0.5s ease-out;
          }
        `}</style>
      </div>
    </>;
};